import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

import { useAuthContext } from '@providers/AuthProvider';

import { BillingAccountService, OrganizationsService, SubscriptionsService } from 'app/API';
import { TSubscriptionProduct } from 'app/types/entities/TSubscriptionProduct';
import { TSubscription } from 'app/types/entities/TSubscription';
import { TLicense } from 'app/types/entities/TLicense';
import { TGetSubscriptionDetailsResponse } from '@queries/subscriptions/getSubscriptionDetails';
import { assignLicense } from '@queries/subscriptions/assignLicense';
import { ModalNamesEnum } from '@constants/enums/ModalNamesEnum';
import { useAPI } from '@hooks/useAPI';
import { useModalContext } from '@providers/ModalProvider';

type TState = {
  isLoading: boolean;
  setIsLoading: (v: boolean) => void;
  search: string;
  setSearch: (v: string) => void;
  removeAssignee: (v: string) => void;
  editTranscriptUnlockType: (v: string, v2: string) => void;
  editAllTranscriptUnlockTypes: () => void;
  editBackupRecordingFlag: (v: string, v2: boolean) => void;
  setGlobalAS1Backup: (v: string) => void;
  unlockAITranscripts: () => void;
  onAddUserClick: (v: string | null) => void;
  assignedCount: number;
  unassignedCount: number;
  onAssignLicense: (v: Assignee, v2: string | null) => void;
  subscription: TSubscription | null;
  plan: TSubscriptionProduct | undefined;
  isAsOneBackupRecordingAllowedFlag: boolean;
  setAsOneBackupRecordingAllowedFlag: (v: boolean) => void;
  licenses: TLicense[];
  globalLicensesBackupEnabledValue: string;
  openAS1BackupTerms: () => void;
};

const PageContext = createContext<TState>({
  isLoading: true,
  setIsLoading: () => {},
  search: '',
  setSearch: () => {},
  removeAssignee: () => {},
  editTranscriptUnlockType: () => {},
  editAllTranscriptUnlockTypes: () => {},
  editBackupRecordingFlag: () => {},
  setGlobalAS1Backup: () => {},
  unlockAITranscripts: () => {},
  onAddUserClick: () => {},
  assignedCount: 0,
  unassignedCount: 0,
  onAssignLicense: () => {},
  subscription: null,
  plan: undefined,
  isAsOneBackupRecordingAllowedFlag: false,
  setAsOneBackupRecordingAllowedFlag: () => {},
  licenses: [],
  globalLicensesBackupEnabledValue: 'Mixed',
  openAS1BackupTerms: () => {},
});

export type TPageProviderProps = {
  children: ReactNode;
  orgSlug: string | undefined;
  aOneId: string | undefined;
};

type Assignee = {
  id: string;
  membershipId: string;
  name: string;
  lastname: string;
  email: string;
};

export const PageProvider = ({ children, aOneId }: TPageProviderProps) => {
  const { call } = useAPI();
  const { openModal, closeModal } = useModalContext();
  const { organization } = useAuthContext();
  const [search, setSearch] = useState('');
  const [plans, setPlans] = useState<TSubscriptionProduct[]>([]);
  const [subscription, setSubscription] = useState<TGetSubscriptionDetailsResponse | null>(null);
  const [licenses, setLicenses] = useState<TLicense[]>([]);
  const [reloadPageTrigger, setReloadPageTrigger] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAsOneBackupRecordingAllowedFlag, setAsOneBackupRecordingAllowedFlag] = useState(false);
  const [globalLicensesBackupEnabledValue, setGlobalLicensesBackupEnabledValue] = useState('Mixed');

  const baId = organization?.billingAccount?.id;

  const load = async () => {
    if (!organization || !baId || !aOneId) {
      console.error('[getLicenses] baid or asOneId was not provided');
      return;
    }
    setIsLoading(true);
    const licenses = await call(SubscriptionsService.getLicenses({ baId, id: aOneId }));
    const backupsGloballyEnabled =
      licenses.find((lic: TLicense) => lic.asOneBackupRecordingAllowed === false) === undefined;
    const backupsGloballyDisabled =
      licenses.find((lic: TLicense) => lic.asOneBackupRecordingAllowed === true) === undefined;
    setGlobalLicensesBackupEnabledValue(backupsGloballyEnabled ? 'On' : backupsGloballyDisabled ? 'Off' : 'Mixed');
    setLicenses(licenses);
    setIsLoading(false);
    const plans = await call(BillingAccountService.getAllAsOnePlans());
    setPlans(plans);
    const subscription = await call(SubscriptionsService.findOne({ baId, id: aOneId ?? '' }));
    setSubscription(subscription);
  };

  useEffect(() => {
    load();
  }, [baId, aOneId, reloadPageTrigger]);

  const plan = plans.find((plan) => plan.id === subscription?.productId);

  const onAssignLicense = async (assignee: Assignee, id: string | null) => {
    if (!baId) {
      return;
    }
    setIsLoading(true);
    closeModal();
    let licenseId: string;
    if (id) {
      licenseId = id;
    } else {
      const license = subscription?.licenses.find((license) => license.assigneeId === null);
      licenseId = license?.id ?? '';
    }
    const result = await assignLicense(baId, {
      subscriptionId: aOneId ?? '',
      licenseId,
      assigneeId: assignee.membershipId,
    });
    if (result.error) {
      openModal(ModalNamesEnum.GenericErrorModal, {
        message: result.error,
      });
    }
    setReloadPageTrigger(!reloadPageTrigger);
  };

  const unassignedCount = subscription?.licenses.filter((lic) => lic.assigneeId === null).length || 0;
  const assignedCount = subscription?.licenses.filter((lic) => lic.assigneeId !== null).length || 0;

  const onAddUserClick = (id: string | null) => {
    openModal(ModalNamesEnum.AddUserLicenseModal, {
      baId,
      subId: aOneId,
      onSuccess: (assignee: Assignee) => onAssignLicense(assignee, id),
    });
  };

  const editTranscriptUnlockType = (licenseId: string, initialValue: string) => {
    openModal(ModalNamesEnum.EditLicenseTranscriptUnlockType, {
      baId,
      subscriptionId: aOneId,
      licenseId,
      initialValue,
      onSuccess: () => setReloadPageTrigger(!reloadPageTrigger),
    });
  };

  const editAllTranscriptUnlockTypes = () => {
    if (!baId) {
      return;
    }
    openModal(ModalNamesEnum.EditSubscriptionTranscriptUnlockType, {
      baId,
      subscriptionId: aOneId,
      initialValue: 'SESSION_TEXT',
      onSuccess: () => setReloadPageTrigger(!reloadPageTrigger),
    });
  };

  const setGlobalAS1Backup = async (v: string) => {
    if (!organization?.asOneBackupRecordingTermsAccepted) {
      openAS1BackupTerms();
      return;
    }
    if (!baId) {
      return;
    }
    await call(
      OrganizationsService.setAsOneBackupRecordingAllowedFlag({
        oid: organization.id,
        subscriptionId: aOneId ?? '',
        requestBody: {
          value: v === '1',
        },
      }),
    );
    load();
  };

  const openAS1BackupTerms = async () => {
    openModal(ModalNamesEnum.AcceptBackupRecordingTerms, {
      onSuccess: async () => {
        await call(
          OrganizationsService.asOneBackupRecordingAcceptTerms({
            oid: organization?.id ?? '',
          }),
        );
        closeModal();
        window.location.reload();
      },
    });
  };

  const editBackupRecordingFlag = (licenseId: string, initialValue: boolean) => {
    if (!organization?.asOneBackupRecordingTermsAccepted) {
      openAS1BackupTerms();
      return;
    }
    if (!baId) {
      return;
    }
    openModal(ModalNamesEnum.UpdateLicenseBackupRecordingFlag, {
      baId,
      licenseId,
      initialValue,
      onSuccess: () => {
        load();
      },
    });
  };

  const removeAssignee = async (licenseId: string) => {
    if (!baId) {
      return;
    }
    setIsLoading(true);
    const result = await assignLicense(baId, {
      subscriptionId: aOneId ?? '',
      licenseId,
      assigneeId: null,
    });
    if (result.error) {
      openModal(ModalNamesEnum.GenericErrorModal, {
        message: result.error,
      });
    }
    setReloadPageTrigger(!reloadPageTrigger);
  };

  const unlockAITranscripts = () => {
    if (!baId) {
      return;
    }
    openModal(ModalNamesEnum.UpgradeToAIFeatures);
  };

  return (
    <PageContext.Provider
      value={{
        isLoading,
        setIsLoading,
        search,
        setSearch,
        removeAssignee,
        editTranscriptUnlockType,
        editAllTranscriptUnlockTypes,
        editBackupRecordingFlag,
        setGlobalAS1Backup,
        unlockAITranscripts,
        onAddUserClick,
        assignedCount,
        unassignedCount,
        onAssignLicense,
        subscription,
        plan,
        isAsOneBackupRecordingAllowedFlag,
        setAsOneBackupRecordingAllowedFlag,
        licenses,
        globalLicensesBackupEnabledValue,
        openAS1BackupTerms,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
